// SeedFromTemplateModal — Phase 3 of the BOQ estimate-template feature.
//
// Calls boq.create_project_from_template() via useBoqSeedFromTemplate(),
// then notifies the parent (which refreshes the dependent BOQ tables).
//
// SD and TD are enabled in the picker; DD/GFC stay disabled for now.
// The subtrade template is single-source: SD is the only stored template and
// TD/DD/GFC mirror it (create_project_from_template falls back to the SD rows).
// The RPC still applies the requested stage's contingency profile and milestone
// kind (td→tender), so a TD seed lands on a `tender` milestone (chip reads "TD").

import React, { useMemo, useState } from 'react';
import {
  BrioModal,
  BrioModalBody,
  BrioModalField,
  BrioInput,
  BrioSelect,
  BrioButton,
} from '../../components/BrioComponents';
import useBoqSeedFromTemplate from '../hooks/useBoqSeedFromTemplate';

const SEEDED_STAGES = new Set(['sd', 'td']); // grow as DD/GFC templates land

export default function SeedFromTemplateModal({ project, taxonomies, onClose, onSeeded }) {
  const specDepthNodes = useMemo(
    () => taxonomies.find((t) => t.code === 'spec_depth')?.nodes || [],
    [taxonomies]
  );

  const defaultStage = SEEDED_STAGES.has(project.schemeStage) ? project.schemeStage : 'sd';
  const [stage, setStage] = useState(defaultStage);
  const [consultant, setConsultant] = useState('Architecture BRIO (in-house QS)');
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const { seedFromTemplate, submitting } = useBoqSeedFromTemplate();

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError(null);
    const result = await seedFromTemplate(project.boqId, {
      stage,
      consultant: consultant.trim() || undefined,
    });
    if (result.success) {
      setSummary(result.summary);
      // Defer close until the parent has refreshed and the user has had a
      // moment to read the toast-like summary.
      if (typeof onSeeded === 'function') {
        await onSeeded(result.summary);
      }
    } else {
      setError(result.error || 'Failed to seed from template.');
    }
  };

  const footer = summary ? (
    <BrioButton variant="primary" onClick={onClose}>Done</BrioButton>
  ) : (
    <>
      <BrioButton variant="secondary" onClick={onClose} disabled={submitting}>Cancel</BrioButton>
      <BrioButton variant="primary" onClick={handleSubmit} disabled={submitting || !project.boqId}>
        {submitting ? 'Seeding…' : 'Seed bill'}
      </BrioButton>
    </>
  );

  return (
    <BrioModal
      title={`Seed bill from estimate template — ${project.projectCode}`}
      onClose={onClose}
      width="md"
      footer={footer}
    >
      <BrioModalBody form>
        {error && (
          <div style={{ padding: '0.75rem 1rem', background: '#fdecec', color: '#a94442', borderRadius: 6, marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {summary ? (
          <div style={{ padding: '0.75rem 1rem', background: '#e8f4ea', color: '#2e7d32', borderRadius: 6, marginBottom: '1rem', fontSize: '0.9rem' }}>
            <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
              Bill seeded ✓
            </div>
            <div>
              {summary.lines_seeded} placeholder lines · {summary.zones_created} zones ·{' '}
              {summary.adjustments} adjustments ({summary.tax}, {summary.contingency_pct}% contingency) ·{' '}
              {summary.clauses} clauses · 1 baseline version snapshot.
            </div>
            <div style={{ marginTop: '0.5rem', fontSize: '0.82rem', color: 'var(--brio-text-muted)' }}>
              Open Bill of Quantities to fill in real quantities and rates.
            </div>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'var(--brio-surface-muted, #f7f7f5)', borderRadius: 6, fontSize: '0.85rem' }}>
              <div><strong>{project.name}</strong></div>
              <div style={{ color: 'var(--brio-text-muted)', marginTop: '0.25rem', fontSize: '0.82rem' }}>
                Seeds the canonical 11-trade structure with a 0-priced placeholder
                under every subtrade, default Contingency + Tax, standard inclusions
                / exclusions / assumptions, and a baseline version snapshot.
              </div>
              <div style={{ color: 'var(--brio-text-muted)', fontSize: '0.78rem', marginTop: '0.4rem' }}>
                Refuses if the project already has bill lines or zones.
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <BrioModalField label="Design stage" hint="Drives template granularity and default contingency.">
                <BrioSelect value={stage} onChange={(e) => setStage(e.target.value)}>
                  {specDepthNodes.length === 0
                    ? <option value="sd">SD — Schematic Design</option>
                    : specDepthNodes.map((n) => {
                        const enabled = SEEDED_STAGES.has(n.code);
                        return (
                          <option key={n.id} value={n.code} disabled={!enabled}>
                            {n.label}{enabled ? '' : ' — template not yet seeded'}
                          </option>
                        );
                      })}
                </BrioSelect>
              </BrioModalField>

              <BrioModalField label="QS / Consultant" hint="Filled into the standard clauses (e.g. assumptions).">
                <BrioInput
                  value={consultant}
                  onChange={(e) => setConsultant(e.target.value)}
                  placeholder="Architecture BRIO (in-house QS)"
                />
              </BrioModalField>
            </form>
          </>
        )}
      </BrioModalBody>
    </BrioModal>
  );
}
